import { useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, ScrollView, useWindowDimensions } from 'react-native';
import { useAppNavigate, usePathname } from '../hooks/useRouteParams';
import { useAppStore } from '../store/appStore';
import { fetchChapters } from '../services/api';
import { saveChapters } from '../services/indexedDb';
import type { Chapter } from '../types';
import { Fonts, MdiIcons, QuranIcons } from '../styles/fonts';
import { useTheme } from '../styles/theme';
import SearchInput from './SearchInput';


const DESKTOP_BREAKPOINT = 768;

export default function ChapterList() {
  const chapters = useAppStore((s) => s.chapters);
  const setChapters = useAppStore((s) => s.setChapters);
  const settings = useAppStore((s) => s.settings);
  const sortReverse = useAppStore((s) => s.settings.sort_chapters_reverse);
  const cardInputType = useAppStore((s) => s.cardInputType);
  const cardInputValue = useAppStore((s) => s.cardInputValue);
  const closeDrawers = useAppStore((s) => s.closeDrawers);
  const setSettings = useAppStore((s) => s.setSettings);
  const setCardInput = useAppStore((s) => s.setCardInput);
  const navigate = useAppNavigate();
  const pathname = usePathname();
  const { colors } = useTheme();
  const { width } = useWindowDimensions();
  const isMobile = width <= DESKTOP_BREAKPOINT;

  // Clear search when this component unmounts (left drawer closes on mobile)
  useEffect(() => {
    return () => { setCardInput('', ''); };
  }, [setCardInput]);

  useEffect(() => {
    if (!chapters || chapters.length === 0) {
      fetchChapters()
        .then((result) => {
          setChapters(result);
          saveChapters(result).catch(() => {});
        })
        .catch(() => {});
    }
  }, [chapters, setChapters]);

  const filtered = chapters.filter((ch) => {
    if (!cardInputValue || cardInputValue.trim() === '') return true;
    const q = cardInputValue.toLowerCase().trim();
    return (
      String(ch.chapter_number).includes(q) ||
      ch.chapter_name_ar_english.toLowerCase().includes(q) ||
      ch.chapter_name_en.toLowerCase().includes(q)
    );
  });

  const sorted: Chapter[] = settings.sort_chapters_reverse
    ? [filtered[0], ...filtered.slice(1).reverse()].filter((ch): ch is Chapter => ch != null)
    : filtered;

  const pathParts = pathname.split('/').filter(Boolean);
  const currentChFromPath = pathParts[0] ? Number(pathParts[0]) : null;

  const handleClick = (chapterNumber: number) => {
    closeDrawers();
    navigate(`/${chapterNumber}`);
  };

  return (
    <View style={styles.container}>
      {!isMobile && <View style={styles.topBarSpacer} />}

      {isMobile && (
        <View style={[styles.toolbar, { backgroundColor: colors.topBarBg }]}>
          <Pressable style={styles.toolbarBtn} onPress={() => setCardInput('search', '')}>
            <Text style={[styles.toolbarIcon, { color: colors.topBarIcon }]}>{MdiIcons.search}</Text>
          </Pressable>
          <Pressable style={styles.toolbarBtn} onPress={() => setSettings({ sort_chapters_reverse: !sortReverse })}>
            <Text style={[styles.toolbarIcon, { color: colors.topBarIcon }, sortReverse && styles.iconActive]}>{MdiIcons.sort}</Text>
          </Pressable>
        </View>
      )}
      {isMobile && cardInputType === 'search' && <SearchInput />}

      <ScrollView style={styles.scroll} contentContainerStyle={styles.list}>
        {sorted.map((ch) => {
          const isSelected =
            currentChFromPath !== null && ch.chapter_number === currentChFromPath;
          return (
            <Pressable
              key={ch.chapter_number}
              style={[
                styles.card,
                { backgroundColor: isSelected ? colors.selectedChapterBg : colors.chapterItemBg },
              ]}
              onPress={() => handleClick(ch.chapter_number)}
            >
              <View style={styles.cardContent}>
                <View style={styles.nameBlock}>
                  <Text style={[styles.chapterEn, { color: isSelected ? colors.selectedChapterText : colors.chapterNameText }]}>
                    {ch.chapter_number}. {ch.chapter_name_ar_english}
                    <Text style={[styles.arabicName, { color: isSelected ? colors.selectedChapterText : colors.chapterNameText }]}>
                      {' - '}
                      {ch.chapter_name_ar_arabic
                        ? ch.chapter_name_ar_arabic.split('سورة ')[1] ?? ch.chapter_name_ar_arabic
                        : ''}
                    </Text>
                  </Text>
                  <Text style={[styles.chapterTrans, { color: isSelected ? '#ccc' : colors.chapterTransText }]}>{ch.chapter_name_en}</Text>
                </View>
                <View style={styles.metaBlock}>
                  <Text style={[styles.revealedIcon, { color: isSelected ? colors.selectedChapterText : colors.primaryText }]}>
                    {ch.revealed_in === 'Meccan' ? QuranIcons.makkah : QuranIcons.madinah}
                  </Text>
                  <Text style={[styles.verseCount, { backgroundColor: colors.verseCountBg, color: colors.verseCountText }]}>
                    {ch.num_of_verses}
                  </Text>
                </View>
              </View>
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  topBarSpacer: {
    height: 50,
  },
  toolbar: {
    height: 50,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  toolbarBtn: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  toolbarIcon: {
    fontFamily: Fonts.MDI,
    fontSize: 24,
  },
  iconActive: {
    opacity: 0.7,
  },
  scroll: {
    flex: 1,
  },
  list: {
    paddingVertical: 8,
    gap: 8,
    paddingBottom: 40,
  },
  card: {
    marginHorizontal: 8,
    borderRadius: 2,
    paddingVertical: 10,
    paddingHorizontal: 12,
    // @ts-expect-error web shadow
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  nameBlock: {
    flex: 1,
  },
  chapterEn: {
    fontSize: 14,
    fontWeight: '700',
  },
  arabicName: {
    fontWeight: '400',
    fontSize: 14,
  },
  chapterTrans: {
    fontSize: 11,
    marginTop: 2,
    marginLeft: 5,
  },
  metaBlock: {
    alignItems: 'center',
    marginLeft: 8,
  },
  revealedIcon: {
    fontFamily: Fonts.Quraniconstwo,
    fontSize: 20,
  },
  verseCount: {
    fontSize: 12,
    marginTop: 2,
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 3,
    width: 35,
    textAlign: 'center',
  },
});
